import { Head, Link } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"

const MONTH_ABBR = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

type MonthCell = {
  id: number
  month_number: number
  month_name: string
  gigs: number
  chart_listings: number
  first_charted: number
  birthdays: number
  deaths: number
}

type YearGroup = { year: number; months: MonthCell[] }

function Chip({ label, value }: { label: string; value: number }) {
  if (value === 0) return null
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] leading-none">
      <span className="text-stone-400 dark:text-stone-500">{label}</span>
      <span className="font-medium text-stone-700 dark:text-stone-300">{value}</span>
    </span>
  )
}

function MonthCard({ cell }: { cell: MonthCell }) {
  const hasData = cell.gigs + cell.chart_listings + cell.first_charted + cell.birthdays + cell.deaths > 0
  return (
    <Link
      href={`/admin/year_month_listings/${cell.id}`}
      className={[
        "block rounded-lg border p-2 no-underline transition-colors",
        hasData
          ? "border-stone-200 dark:border-stone-700 hover:border-amber-400 dark:hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20"
          : "border-stone-100 dark:border-stone-800 opacity-40 pointer-events-none"
      ].join(" ")}
    >
      <p className="text-xs font-semibold text-stone-700 dark:text-stone-300 mb-1.5">
        {MONTH_ABBR[cell.month_number]}
      </p>
      <div className="flex flex-col gap-0.5">
        <Chip label="G"  value={cell.gigs} />
        <Chip label="CL" value={cell.chart_listings} />
        <Chip label="FC" value={cell.first_charted} />
        <Chip label="B"  value={cell.birthdays} />
        <Chip label="D"  value={cell.deaths} />
      </div>
    </Link>
  )
}

function PlaceholderMonth({ monthNumber }: { monthNumber: number }) {
  return (
    <div className="block rounded-lg border border-stone-100 dark:border-stone-800 p-2 opacity-25">
      <p className="text-xs font-semibold text-stone-400 dark:text-stone-600">{MONTH_ABBR[monthNumber]}</p>
    </div>
  )
}

export default function YearMonthListingsIndex({ years }: { years: YearGroup[] }) {
  return (
    <>
      <Head title="Year-Month Listings — Admin">
        <meta name="robots" content="noindex" />
      </Head>
      <AdminShell>
        <div className="flex items-baseline justify-between mb-6">
          <h1>Year-Month Listings</h1>
          <p className="text-xs text-stone-400 dark:text-stone-500">
            G = gigs &nbsp;·&nbsp; CL = chart listings &nbsp;·&nbsp; FC = first charted &nbsp;·&nbsp; B = birthdays &nbsp;·&nbsp; D = deaths
          </p>
        </div>

        <div className="space-y-3">
          {years.map(({ year, months }) => {
            const monthMap = new Map(months.map((m) => [m.month_number, m]))
            return (
              <div key={year} className="flex items-start gap-3">
                <div className="w-12 shrink-0 pt-2.5 text-right">
                  <span className="text-sm font-semibold text-stone-500 dark:text-stone-400">{year}</span>
                </div>
                <div className="grid grid-cols-12 gap-1.5 flex-1">
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((mn) => {
                    const cell = monthMap.get(mn)
                    return cell
                      ? <MonthCard key={mn} cell={cell} />
                      : <PlaceholderMonth key={mn} monthNumber={mn} />
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </AdminShell>
    </>
  )
}
